/* eslint-disable max-len */
import {
  Empty,
  Select
} from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/export-data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/offline-exporting';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getConvertedPrice, localShortDateFormat } from '../../../../../utils/DateAndNumberFunctions';
import { formatLargeNumber } from '../../../../MaterialDetails/tabElements/TabComponents/tabGraph/TabGraphHelpers';
import styles from '../styles.module.scss';
import TextWidgetContainer from '../textWidget/TextWidgetContainer';
import { getOptInvByTimeData } from '../utils/WidgetFunctions';
import { widgetTypeKeys } from '../utils/widgetTypes';
import '../Widgets.css';

export default function LineChartWidget({
  theme, inventoryStatus, canViewValues,
  widgetID, timePeriod, setTimePeriod,
  textWidgetID,
}) {
  const defaultCurrency = useSelector((state) => state.commonState?.userSettings?.DEFAULT_CURRENCY);
  const dashboardFilter = useSelector((state) => state.commonState.localDashboardFilters);
  const currencyList = useSelector((state) => state.commonState?.currencyList);
  const gridData = useSelector((state) => state.dashboardState.gridData);
  const [data, setData] = useState([]);
  const [maxValue, setMaxValue] = useState();
  const [minValue, setMinValue] = useState();
  const [yAxisText, setYAxisText] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [valueSuffix, setValueSuffix] = useState('');

  const { Option } = Select;

  const setMinMaxVals = (formattedData) => {
    if (!formattedData?.length) return undefined;
    let maxVal;
    let minVal = Infinity;
    formattedData?.forEach((line) => {
      line?.data?.forEach((point) => {
        if (!maxVal && maxVal !== 0) maxVal = point?.y ?? 0;
        if (point?.y > maxVal) {
          maxVal = point.y;
        }
        if ((point?.y || point?.y === 0) && minVal > point.y) minVal = point.y;
      });
    });
    if (minVal !== Infinity) {
      maxVal += ((maxVal - minVal) * 0.2);
      minVal -= ((maxVal - minVal) * 0.2);
    } else {
      maxVal += maxVal * 0.1;
      minVal = 0;
    }
    let finalMax = Math.ceil(maxVal);
    let finalMin = Math.floor(minVal, 10);
    finalMin = minVal;
    finalMax = maxVal;
    // finalMin -= ((finalMax - finalMin) * 0.3);
    if (finalMax > 99 && widgetID === widgetTypeKeys.optimalInvByTime) finalMax = 100;
    if (finalMax === finalMin) finalMin -= 2;

    // set tickValues for graph
    const tickVals = [];
    const tickSize = (finalMax - finalMin) / 6;
    if (widgetID === widgetTypeKeys.optimalInvByTime) {
      if (finalMax === finalMin) finalMin = 0;
      if (finalMax > 100) finalMax = 100;
      if (finalMin < 0) finalMin = 0;
    }
    tickVals.push(finalMin);
    for (let i = finalMin + tickSize; i < finalMax; i += tickSize) {
      tickVals.push(i);
    }
    tickVals[6] = finalMax;
    setMaxValue(finalMax);
    setMinValue(finalMin);
    return finalMax;
  };

  const getLeftAxisText = (numText) => {
    if (widgetID !== widgetTypeKeys.inventoryValByTime) return 'Percentage';
    if (numText === '' || !numText) return 'Inventory Value';
    return `Inv. value in ${numText}`;
  };

  const getMinVal = (formattedData) => {
    let minVal;
    formattedData?.forEach((line) => {
      line?.data?.forEach((point) => {
        if (!minVal && minVal !== 0) minVal = point?.y ?? 0;
        if (point?.y < minVal) {
          minVal = point.y;
        }
      });
    });
    return minVal;
  };

  const getConvertedCurrency = (currVal) => {
    let convertedVal = currVal;
    let curr = 'NOK';
    if (defaultCurrency && defaultCurrency !== 'NOK') {
      const temp = currencyList?.find((x) => defaultCurrency === x?.FROM_CURRENCY);
      convertedVal = getConvertedPrice(
        currVal, temp?.FROM_RATIO, temp?.TO_RATIO, temp?.EXCHANGE_RATE,
      );
      curr = defaultCurrency;
    }
    return ({
      convertedVal,
      curr,
    });
  };

  const getInventoryValue = (val = 0, suffix) => {
    let curr = 'NOK';
    if (defaultCurrency && defaultCurrency !== 'NOK') {
      curr = defaultCurrency;
    }
    return `${formatLargeNumber(val, null, 2)}${suffix} ${curr}`;
  };

  const convertInvValToSelectedCurrency = (inv) => {
    const tempInv = _.cloneDeep(inv);
    inv?.forEach((line, i) => line?.data?.forEach((node, j) => {
      const { convertedVal } = getConvertedCurrency(Number.parseInt(node?.y ?? 0, 10));
      tempInv[i].data[j].y = convertedVal;
    }));
    return tempInv;
  };

  const formatInventorydata = (inv) => {
    const formattedData = convertInvValToSelectedCurrency(inv);
    let minVal = getMinVal(formattedData);
    minVal = formatLargeNumber(minVal);
    let divisor = 1;
    if (minVal?.includes('G')) {
      setValueSuffix('G');
      setYAxisText('Billions');
      divisor = 1000000000;
    } else if (minVal?.includes('M')) {
      setValueSuffix('M');
      setYAxisText('Millions');
      divisor = 1000000;
    } else if (minVal?.includes('K')) {
      setValueSuffix('K');
      setYAxisText('Thousands');
      divisor = 1000;
    }
    const tempInv = _.cloneDeep(inv);
    formattedData?.forEach((line, i) => line?.data?.forEach((node, j) => {
      tempInv[i].data[j].yOriginal = node?.y;
      tempInv[i].data[j].y = (node?.y ?? 0) / divisor;
    }));
    return tempInv;
  };

  const dataNotNull = (invData) => {
    let flag = false;
    invData?.forEach((grp) => {
      if (grp && typeof grp === 'object') {
        const grpName = Object.keys(grp)?.[0];
        if (grpName && grp[grpName]?.data?.length > 0) {
          flag = true;
        }
      }
    });
    return flag;
  };

  // const handleStartDateChange = (date) => {
  //   setStartDate(date);
  // };

  // const handleEndDateChange = (date) => {
  //   setEndDate(date);
  // };

  const [resolvedColors, setResolvedColors] = useState(null);
  const [containerNode, setContainerNode] = useState(null);
  const containerRef = React.useCallback((node) => {
    setContainerNode(node);
  }, []);

  const getAllDates = (xCategories) => {
    const dateSet = new Set();
    xCategories?.forEach(
      (grp) => grp?.data?.forEach(({ x }) => dateSet.add(x))
    );
    let sortedDates = Array.from(dateSet);
    sortedDates = sortedDates?.sort(
      (a, b) => new Date(a)?.getTime() - new Date(b)?.getTime()
    );
    return sortedDates;
  };

  useEffect(() => {
    if (!inventoryStatus?.data?.length) return;
    if (!dataNotNull(inventoryStatus?.data)) {
      setData(null);
      return;
    }
    let formattedData = getOptInvByTimeData(
      inventoryStatus.data,
      dashboardFilter ?? null,
      widgetID,
      inventoryStatus.mode,
    );
    if (!formattedData.length) {
      setMaxValue(1);
      formattedData = [];
    } else if (widgetID === widgetTypeKeys.inventoryValByTime) {
      formattedData = formatInventorydata(formattedData);
    }
    setMinMaxVals(formattedData);
    const allDates = getAllDates(formattedData);
    const finalData = [];
    formattedData?.forEach((grp) => {
      const newGrp = {
        ...grp,
        data: []
      };
      allDates?.forEach((x) => {
        const point = grp?.data?.find((node) => node?.x === x);
        if (!point) newGrp.data.push([]);
        else newGrp.data.push(point);
      });
      finalData.push(newGrp);
    });
    setData(finalData);
  }, [inventoryStatus, dashboardFilter]);

  const chartComponentRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(300);

  useEffect(() => {
    if (containerNode) {
      const computedStyle = getComputedStyle(containerNode);
      setResolvedColors({
        neutral100: computedStyle.getPropertyValue('--neutral--100').trim(),
        neutral400: computedStyle.getPropertyValue('--neutral--400').trim(),
        neutral600: computedStyle.getPropertyValue('--neutral--600').trim(),
        neutral800: computedStyle.getPropertyValue('--neutral--800').trim(),
        tooltipText: computedStyle.getPropertyValue('--tooltip-text-color').trim(),
        tooltipBg: computedStyle.getPropertyValue('--widget-tooltip').trim(),
        widgetBg: computedStyle.getPropertyValue('--widget-background').trim(),
      });
    }
  }, [containerNode]);

  useEffect(() => {
    if (!containerNode) return;
    const updateDimensions = () => {
      if (!document.fullscreenElement) {
        const width = containerNode.offsetWidth;
        if (!gridData) {
          setChartHeight((width * 0.33) - 40);
          return;
        }
        const totalHeight = gridData?.gridHeight;
        let finalHeight = totalHeight;
        if (gridData?.colCount === 3) {
          finalHeight = (totalHeight / 3) - 60;
        }
        setChartHeight(finalHeight);
      } else {
        setChartHeight(window.innerHeight);
      }
    };

    // Initial measurement
    updateDimensions();

    // Add resize listener
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerNode);

    // Cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerNode, gridData]);

  const getTooltipContent = (point, suffix) => {
    if (widgetID === widgetTypeKeys.optimalInvByTime) {
      return `
        <div style="white-space: nowrap;">
          <strong>Optimal inventory: </strong>
          ${point.y.toFixed(2)}%
        </div>`;
    }

    if (canViewValues) {
      const inventoryValue = getInventoryValue(point.options.y ?? 0, suffix);
      const countOfMaterials = point.options ? point.options.count : '-';
      return `
        <div style="white-space: nowrap;">
          <strong>Value of inventory:</strong>
          ${inventoryValue}
        </div>
        <div style="white-space: nowrap;">
          <strong>Count of materials:</strong>
           ${countOfMaterials}
        </div>`;
    }

    return '';
  };

  const getLabelAngles = (dataPoints, defaultAngle) => {
    const nodes = dataPoints?.[0]?.data;
    let angle = -15;
    if (!nodes?.length) return undefined;
    if (nodes?.length > 11) angle = -20;
    if (nodes?.length > 16) angle = -30;
    if (defaultAngle) angle = defaultAngle;
    return nodes?.map(() => angle);
  };

  const formatDate = (dt) => {
    const parts = dt?.split('.');
    if (!parts?.length) return dt;
    const year = parts[0]?.slice(2, 4);
    const month = parts[1].padStart(2, '0');
    const day = parts[2].padStart(2, '0');

    return day.concat('.', month, '.', year);
  };

  // formatDate(x)
  const getCategories = (xCategories) => {
    const cats = getAllDates(xCategories)?.map((x) => formatDate(x));
    return cats;
  };

  const fontSizeForExport = {
    endOnTick: false,
    title: {
      style: {
        ...(theme.xAxis?.labels?.style || {}),
        fontSize: 'max(0.85vw, calc((0.85 * 1.7778) * 1vh))',
      }
    },
    labels: {
      style: {
        ...(theme.xAxis?.labels?.style || {}),
        fontSize: 'max(0.85vw, calc((0.85 * 1.7778) * 1vh))',
      }
    },
  };

  const options = {
    ...theme,
    exporting: {
      enabled: true,
      url: '',
      local: true,
      // scale: ,
      fallbackToExportServer: false,
      error: (_opt, err) => console.log(err),
      menuItemDefinitions: {
        viewData: {
          text: 'View data table'
        }
      },
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'downloadPNG',
          ],
          symbol: 'menu',
          symbolX: 3,
          // symbolY: 3,
          y: -6,
          symbolFill: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStroke: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStrokeWidth: 1,
          theme: {
            fill: 'transparent',
            states: {
              hover: {
                fill: 'transparent',
              },
              select: {
                fill: 'transparent',
              }
            }
          }
        }
      },
      chartOptions: {
        chart: {
          marginTop: 0,
          backgroundColor: resolvedColors?.widgetBg,
        },
        colors: [
          '#1f77b4',
          '#ff7f0e',
          '#2ca02c',
          '#d62728',
          '#9467bd',
          '#8c564b',
          '#e377c2',
          '#7f7f7f',
          '#bcbd22',
        ],
        yAxis: fontSizeForExport,
        xAxis: fontSizeForExport,
        plotOptions: {
          area: {
            dataLabels: {
              enabled: true,
              style: fontSizeForExport.labels.style
            }
          }
        },
        legend: {
          enabled: true,
          itemStyle: fontSizeForExport.labels.style
        }
      }
    },
    navigation: {
      buttonOptions: {
        align: 'right',
        verticalAlign: 'top',
        y: 0,
        x: 10,
        theme: {
          padding: 8
        }
      },
      menuStyle: {
        background: 'var(--neutral--800)',
        color: 'var(--neutral--100)'
      },
      menuItemStyle: {
        color: 'var(--neutral--100)',
        fontSize: '14px',
        padding: '0.5em 1em',
        transition: 'background 200ms',
      },
      menuItemHoverStyle: {
        background: 'var(--neutral--600)',
        color: 'var(--neutral--100)'
      }
    },
    chart: {
      ...theme.chart,
      type: 'line',
      height: chartHeight,
      marginTop: 12,
      spacing: [0, 8, 0, 8],
      animation: false,
      backgroundColor: 'var(--widget-background)',
    },
    title: {
      text: undefined,
    },
    tooltip: {
      ...theme.tooltip,
      useHTML: true,
      formatter() {
        const { point, series } = this;
        const tooltipContent = getTooltipContent(
          point,
          valueSuffix,
        );

        return `<div style="padding: 12px">
          <div style="white-space: nowrap;">
            <strong>
              ${inventoryStatus?.mode === 'companyCode' ? 'Company Code:' : 'Plant:'}
            </strong>
            ${series.name}
          </div>
          <div style="white-space: nowrap;"><strong>Date:</strong> ${localShortDateFormat(point.category)}</div>
          ${tooltipContent}
        </div>`;
      },
    },
    xAxis: {
      ...theme.xAxis,
      type: 'datetime',
      labels: {
        ...theme.xAxis?.labels,
        style: {
          ...(theme.xAxis?.labels?.style || {}),
          fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        },
        // To disable rotation this property must be set to undefined
        autoRotation: getLabelAngles(data),
        // rotation: getLabelAngles(data, -15),
        // align: data?.[0]?.data?.length <= 11 ? 'center' : 'center',
        padding: 1,
        // y: 15,
        margin: 0,
        allowOverlap: false,
      },
      lineWidth: 1,
      lineColor: 'var(--linechart-color-10)',
      tickWidth: 1,
      tickLength: 7,
      tickmarkPlacement: 'on',
      tickColor: 'var(--linechart-color-10)',
      crosshair: {
        color: 'var(--linechart-color-11)',
        width: 1,
        dashStyle: 'LongDash',
      },
      categories: getCategories(data)
    },
    yAxis: {
      ...theme.yAxis,
      ceiling: widgetID === widgetTypeKeys.optimalInvByTime ? 100 : undefined,
      max: widgetID === widgetTypeKeys.optimalInvByTime ? maxValue : undefined,
      min: widgetID === widgetTypeKeys.optimalInvByTime ? minValue : undefined,
      title: {
        text: getLeftAxisText(yAxisText),
        style: {
          fontSize: 'max(0.65vw, calc((0.65 * 1.7778) * 1vh))',
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          textOutline: 'none',
        },
      },
      labels: {
        ...theme.yAxis?.labels,
        formatter() {
          const { value } = this;
          const formatted = Number.parseFloat(value).toFixed(1);
          if (formatted > 100 && widgetID === widgetTypeKeys.optimalInvByTime) return '';
          return formatted > 0 ? formatted : '';
        },
        style: {
          fontSize: 'max(0.6vw, calc((0.65 * 1.7778) * 1vh))',
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          textOutline: 'none',
        },
        // x: gridData?.colCount === 1 ? -3 : -15,
        allowOverlap: false,
      },
      crosshair: {
        color: 'var(--linechart-color-11)',
        width: 1,
        dashStyle: 'LongDash',
      },
      distance: 5,
      lineWidth: 1,
      lineColor: 'var(--linechart-color-10)',
      tickWidth: 1,
      tickLength: gridData?.colCount === 1 ? 1 : 7,
      tickPosition: 'outside',
      tickmarkPlacement: 'on',
      tickColor: 'var(--linechart-color-10)',
      tickAmount: document.fullscreenElement ? 20 : 5,
      endOnTick: true,
      gridLineWidth: 1,
      minorGridLineWidth: 1,
      // gridLineColor: 'var(--input-border)',
    },
    plotOptions: {
      series: {
        states: {
          inactive: {
            opacity: 0.4
          }
        }
      },
      line: {
        marker: {
          enabled: false,
          radius: 3,
          states: {
            hover: {
              enabled: true,
              radius: 4,
              lineWidth: 1,
              lineColor: resolvedColors?.neutral100 || 'var(--neutral--100)',
              // fillColor: resolvedColors?.neutral100 || 'var(--neutral--100)'
            }
          }
        },
        dataLabels: {
          enabled: true,
          format: '{y:.2f}',
          y: 0,
          verticalAlign: 'top',
          // alignTo: 'plotEdges',
          align: 'center',
          style: {
            color: resolvedColors?.neutral100 || 'var(--neutral--100)',
            textOutline: 'none',
            fontWeight: 'normal',
          },
        },
        animation: true
      },
      area: {
        fillColor: 'transparent',
        marker: {
          enabled: true,
          radius: 4,
          lineWidth: 1,
          // fillColor: resolvedColors?.neutral100 || 'var(--neutral--100)',
          states: {
            hover: {
              enabled: true,
              radius: 4,
              lineWidth: 2,
            }
          }
        },
        lineWidth: 2,
        states: {
          hover: {
            lineWidth: 2
          }
        },
        threshold: null,
        dataLabels: {
          enabled: true,
          format: '{y:.2f}',
          y: 0,
          verticalAlign: 'top',
          // alignTo: 'plotEdges',
          align: 'center',
          style: {
            color: 'var(--neutral--100)',
            textOutline: 'none',
            fontWeight: 'normal',
            fontSize: 'max(0.57vw, calc((0.57 * 1.7778) * 1vh))'
          },
        },
      }
    },
    colors: [
      'var(--linechart-color-1)',
      'var(--linechart-color-2)',
      'var(--linechart-color-3)',
      'var(--linechart-color-4)',
      'var(--linechart-color-5)',
      'var(--linechart-color-6)',
      'var(--linechart-color-7)',
      'var(--linechart-color-8)',
      'var(--linechart-color-9)',
    ],
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        color: 'var(--neutral--400)'
      },
      margin: 0,
      // y: 0,
      // itemMarginTop: -15,
      symbolRadius: 0,
      itemHoverStyle: {
        background: 'rgba(0, 0, 0, 0.3)',
        opacity: 1,
      },
    },
    series: data?.map((series) => ({
      type: 'area',
      name: series.id,
      data: series.data.map((point) => ({
        y: point.y,
        // x: point.x,
        count: point.count,
      })),
      dataLabels: {
        enabled: true,
        allowOverlap: true,
        verticalAlign: 'top',
      }
    })),
    credits: {
      enabled: false,
    },

  };

  return (
    data?.length > 0 ? (
      <>
        <div className={styles.datePickerContainer}>
          {
          // <DatePicker
          //   placeholder="Start Date"
          //   onChange={handleStartDateChange}
          //   format="DD/MM/YYYY"
          //   style={{
          //     width: '100px',
          //     zIndex: 9,
          //     padding:
          //     '0px 4px',
          //     fontSize: '12px',
          //     height: '24px'
          //   }}
          // />
          // <ArrowRightOutlined style={{ fontSize: '12px', color: 'var(--neutral--400)' }} />
          // <DatePicker
          //   placeholder="End Date"
          //   onChange={handleEndDateChange}
          //   format="DD/MM/YYYY"
          //   style={{
          //     width: '100px',
          //     zIndex: 9,
          //     padding: '0px 4px',
          //     fontSize: '12px',
          //     height: '24px'
          //   }}
          // />
          }
          <Select
            style={{
              width: '100px',
              zIndex: 9,
              padding: '0px 4px',
              fontSize: '12px',
              height: '24px'
            }}
            onChange={(e) => setTimePeriod(e)}
            value={timePeriod}
            className="topRightButton"
          >
            <Option key="WEEK" value="WEEK" style={{ fontSize: '12px', height: '24px' }}>Weekly</Option>
            <Option key="MONTH" value="MONTH" style={{ fontSize: '12px', height: '24px' }}>Monthly</Option>
            <Option key="YEAR" value="YEAR" style={{ fontSize: '12px', height: '24px' }}>Yearly</Option>
          </Select>
        </div>
        <div className={styles.lineChartWidget}>
          <div className={styles.widgetText}>
            {
            textWidgetID
              ? (
                <TextWidgetContainer
                  widgetID={textWidgetID}
                />
              )
              : null
          }
          </div>
          <div ref={containerRef} className={styles.chartContainer}>
            <HighchartsReact
              highcharts={Highcharts}
              options={{
                ...options,
              }}
              ref={chartComponentRef}
            />
          </div>
        </div>
      </>
    ) : (
      <div style={{ textAlign: 'center' }}>
        {textWidgetID && (
        <TextWidgetContainer
          widgetID={textWidgetID}
        />
        )}
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No historical data" />
      </div>
    )
  );
}

LineChartWidget.defaultProps = {
  timePeriod: 'MONTH',
  textWidgetID: null,
};

LineChartWidget.propTypes = {
  inventoryStatus: PropTypes.objectOf(PropTypes.any).isRequired,
  canViewValues: PropTypes.bool.isRequired,
  widgetID: PropTypes.string.isRequired,
  timePeriod: PropTypes.string,
  setTimePeriod: PropTypes.func.isRequired,
  textWidgetID: PropTypes.string,
  theme: PropTypes.objectOf(PropTypes.any).isRequired,
};
